import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Link,
} from '@react-pdf/renderer';
import { parsePhoneNumber } from 'libphonenumber-js';
import tinycolor from 'tinycolor2';
import { Report } from '../types';
import { Company } from '@/util/types/serverTypes';
import { RepairItem } from '@/util/types/serverTypeCollection/repairList';
import {
  isSpectoraRepairItem,
  repairItemDefectIdx,
  repairItemLocation,
  repairItemName,
  repairItemTextHtml,
  repairItemThumbs,
} from '../utils/repairItemAccessors';
import { getInspectionDisplayAddress } from '@/util/inspection/getInspectionDisplayAddress';

// Register fonts (optional - using built-in fonts for now)
// You can add custom fonts later if needed

interface RepairListPDFProps {
  logoDataUri?: string | null;
  repairItems: RepairItem[];
  report: Report;
  company: Company;
  /** When provided, used as the document title instead of "Repair List". */
  repairListName?: string | null;
  /** When false, item notes are omitted from the PDF. Defaults to true. */
  includeNotes?: boolean;
}

// Helper function to format phone number
const formatPhoneNumber = (phone: string): string => {
  try {
    const phoneNumber = parsePhoneNumber(phone, 'US');
    return phoneNumber?.formatNational() || phone;
  } catch (error) {
    return phone;
  }
};

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 40,
    fontSize: 10,
    lineHeight: 1.4,
  },

  // Header styles
  header: {
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: '#e5e7eb',
    paddingBottom: 5,
  },

  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 10,
    textAlign: 'center',
  },

  subtitle: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 10,
  },

  // Report info grid
  infoGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },

  infoSection: {
    flex: 1,
    marginHorizontal: 5,
    padding: 10,
    backgroundColor: '#f9fafb',
    borderRadius: 4,
  },

  infoTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
    paddingBottom: 4,
  },

  infoItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },

  infoLabel: {
    fontSize: 9,
    color: '#6b7280',
    width: 60,
    fontWeight: 'bold',
  },

  infoValue: {
    fontSize: 9,
    color: '#374151',
    flex: 1,
  },

  // Company header with logo
  companyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
  },

  companyLogo: {
    height: 26,
    marginRight: 15,
    objectFit: 'contain',
  },

  companyInfo: {
    flex: 1,
  },

  companyName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 4,
  },

  companyPhone: {
    fontSize: 10,
    color: '#6b7280',
  },

  // Repair items styles
  itemsSection: {
    marginTop: 20,
  },

  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: '#3b82f6',
    paddingBottom: 6,
  },

  reportGroupHeader: {
    backgroundColor: '#f3f4f6',
    padding: 8,
    marginBottom: 8,
    marginTop: 12,
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: '#3b82f6',
  },

  reportGroupTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#374151',
  },

  repairItem: {
    marginBottom: 12,
    padding: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#fafafa',
    pageBreakInside: false,
    wrap: false,
    orphans: 3,
    widows: 3,
  },

  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },

  itemInfo: {
    flex: 1,
    marginRight: 15,
  },

  itemName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 4,
  },

  itemLocation: {
    fontSize: 10,
    color: '#6b7280',
    marginBottom: 2,
  },

  itemSection: {
    fontSize: 10,
    color: '#3b82f6',
    fontWeight: 'bold',
  },

  // Photo grid styles
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginVertical: 6,
    pageBreakInside: false,
  },

  photoGridItem: {
    width: '32.5%',
    marginRight: 4,
    marginBottom: 4,
  },

  photoGridItemLast: {
    width: '32.5%',
    marginRight: 0,
    marginBottom: 4,
  },

  gridPhoto: {
    width: '100%',
    borderRadius: 4,
  },

  photoCaption: {
    fontSize: 7,
    color: '#6b7280',
    marginTop: 2,
    lineHeight: 1.2,
    maxHeight: 24,
    overflow: 'hidden',
  },

  itemContent: {
    marginTop: 4,
    pageBreakInside: false,
  },

  itemText: {
    fontSize: 10,
    color: '#374151',
    marginBottom: 8,
    lineHeight: 1.5,
  },

  itemNotes: {
    fontSize: 10,
    color: '#059669',
    fontStyle: 'italic',
    backgroundColor: '#f0fdf4',
    padding: 8,
    borderRadius: 4,
  },

  // Addendum (request repair / credit) – below item content
  addendumBox: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  addendumLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#6b7280',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  addendumRepair: {
    backgroundColor: '#fffbeb',
    borderWidth: 1,
    borderColor: '#f59e0b',
    borderRadius: 6,
    padding: 8,
  },
  addendumRepairTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#92400e',
    marginBottom: 2,
  },
  addendumRepairSub: {
    fontSize: 9,
    color: '#b45309',
  },
  addendumCredit: {
    backgroundColor: '#ecfdf5',
    borderWidth: 1,
    borderColor: '#10b981',
    borderRadius: 6,
    padding: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  addendumCreditLeft: {
    flex: 1,
  },
  addendumCreditTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#065f46',
    marginBottom: 2,
  },
  addendumCreditSub: {
    fontSize: 9,
    color: '#047857',
  },
  addendumCreditAmount: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#065f46',
  },

  itemValue: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
    backgroundColor: '#f3f4f6',
    padding: 8,
    borderRadius: 4,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },

  // Category badge
  categoryBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: '#fbbf24',
    color: '#92400e',
    padding: 4,
    borderRadius: 3,
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 1,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 10,
  },

  // Summary box
  summaryRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 2,
  },
  summaryBox: {
    flex: 1,
    backgroundColor: '#eff6ff',
    borderWidth: 1,
    borderColor: '#3b82f6',
    borderRadius: 6,
    padding: 10,
  },
  summaryTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 4,
  },
  summaryText: {
    fontSize: 8,
    color: '#374151',
    lineHeight: 1.3,
  },
  addendumSummaryBox: {
    flex: 1,
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#fafafa',
  },
  addendumSummaryTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 6,
  },
  addendumSummaryRepair: {
    backgroundColor: '#fffbeb',
    borderColor: '#f59e0b',
    borderWidth: 1,
    borderRadius: 4,
    padding: 6,
    marginBottom: 4,
  },
  addendumSummaryCredit: {
    backgroundColor: '#ecfdf5',
    borderColor: '#10b981',
    borderWidth: 1,
    borderRadius: 4,
    padding: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  addendumSummaryRepairText: {
    fontSize: 9,
    color: '#92400e',
    fontWeight: 'bold',
  },
  addendumSummaryCreditText: {
    fontSize: 9,
    color: '#065f46',
    fontWeight: 'bold',
  },
  addendumSummaryCreditAmount: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#065f46',
  },

  // Video styles
  videoIndicator: {
    position: 'absolute',
    top: 5,
    left: 5,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 3,
    padding: 2,
  },

  videoIndicatorText: {
    color: '#ffffff',
    fontSize: 8,
    fontWeight: 'bold',
  },

  videoLinks: {
    marginTop: 8,
    padding: 8,
    backgroundColor: '#f0f9ff',
    borderRadius: 4,
  },

  videoLinksTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 4,
  },

  videoLink: {
    fontSize: 8,
    color: '#3b82f6',
    marginBottom: 2,
    lineHeight: 1.2,
  },

  videoLinkText: {
    fontSize: 8,
    color: '#3b82f6',
    textDecoration: 'underline',
  },
});

const RepairListPDF = ({
  repairItems,
  report,
  company,
  logoDataUri,
  repairListName,
  includeNotes = true,
}: RepairListPDFProps) => {
  // Generate company color variants
  const primaryColor = company.color || '#3b82f6';
  const lightBg = tinycolor(primaryColor).setAlpha(0.1).toHex8String();
  const veryLightBg = tinycolor(primaryColor).setAlpha(0.05).toHex8String();
  const darkColor = tinycolor(primaryColor).darken(20).toHexString();

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const stripHtml = (html: string): string => {
    if (!html) return '';

    // Remove HTML tags
    let text = html.replace(/<[^>]*>/g, '');

    // Decode common HTML entities
    text = text
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&hellip;/g, '...')
      .replace(/&mdash;/g, 'N/A')
      .replace(/&ndash;/g, '–');

    // Clean up extra whitespace
    text = text.replace(/\s+/g, ' ').trim();

    return text;
  };

  /** In browser, use same-origin proxy for cross-origin image URLs to avoid CORS. */
  const proxyIfCrossOrigin = (url: string): string => {
    if (!url || typeof window === 'undefined') return url;
    try {
      if (!url.startsWith('http')) return url;
      if (new URL(url).origin === window.location.origin) return url;
      return `/api/proxy-image?url=${encodeURIComponent(url)}`;
    } catch {
      return url;
    }
  };

  const getCategoryLabel = (category: number) => {
    switch (category) {
      case -1:
        return 'Maintenance';
      case 0:
        return 'Repair';
      case 1:
        return 'Critical';
      default:
        return 'Standard';
    }
  };

  const getCategoryColor = (category: number) => {
    switch (category) {
      case -1:
        return '#3b82f6'; // Blue
      case 0:
        return '#f59e0b'; // Orange
      case 1:
        return '#ef4444'; // Red
      default:
        return '#6b7280'; // Gray
    }
  };

  const addendumRepairCount = repairItems.filter(
    (i) => i.addendumType === 'repair'
  ).length;
  const addendumCreditItems = repairItems.filter(
    (i) => i.addendumType === 'credit'
  );
  const addendumCreditCount = addendumCreditItems.length;
  const addendumCreditTotal = addendumCreditItems.reduce(
    (sum, i) => sum + (Number(i.addendumCreditAmount) || 0),
    0
  );
  const hasAddendum = addendumRepairCount > 0 || addendumCreditCount > 0;

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        {/* Company Header */}
        <View style={styles.companyHeader}>
          {(logoDataUri || company.logo) && (
            <Image
              style={styles.companyLogo}
              src={logoDataUri || company.logo || ''}
            />
          )}
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>{company.name}</Text>
            {company.email && (
              <Text style={styles.companyPhone}>{company.email}</Text>
            )}
            {company.phone && (
              <Text style={styles.companyPhone}>
                {formatPhoneNumber(company.phone)}
              </Text>
            )}
          </View>
        </View>

        {/* Document Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{repairListName?.trim() || 'Repair List'}</Text>
          <Text style={styles.subtitle}>{report.name}</Text>

          {/* Report Information Grid */}
          <View style={styles.infoGrid}>
            {/* Inspection Details */}
            <View style={styles.infoSection}>
              <Text style={styles.infoTitle}>Inspection Details</Text>
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Date:</Text>
                <Text style={styles.infoValue}>
                  {formatDate(report.inspection.date)}
                </Text>
              </View>
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Address:</Text>
                <Text style={styles.infoValue}>
                  {getInspectionDisplayAddress(report.inspection)}
                </Text>
              </View>
            </View>

            {/* Inspector */}
            <View style={styles.infoSection}>
              <Text style={styles.infoTitle}>Inspector</Text>
              {report.inspectors.map((inspector, index) => (
                <View key={index}>
                  <View style={styles.infoItem}>
                    <Text style={styles.infoLabel}>Name:</Text>
                    <Text style={styles.infoValue}>{inspector.name}</Text>
                  </View>
                  {inspector.credentials && (
                    <View style={styles.infoItem}>
                      <Text style={styles.infoLabel}>Credentials:</Text>
                      <Text style={styles.infoValue}>
                        {inspector.credentials}
                      </Text>
                    </View>
                  )}
                </View>
              ))}
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Company:</Text>
                <Text style={styles.infoValue}>{company.name}</Text>
              </View>
            </View>

            {/* People */}
            <View style={styles.infoSection}>
              <Text style={styles.infoTitle}>People</Text>
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Client:</Text>
                <Text style={styles.infoValue}>
                  {report.inspection.buyer.name}
                </Text>
              </View>
              {report.buying_agent && (
                <>
                  <View style={styles.infoItem}>
                    <Text style={styles.infoLabel}>Agent:</Text>
                    <Text style={styles.infoValue}>
                      {report.buying_agent.name}
                    </Text>
                  </View>
                  {report.buying_agent.agency_name && (
                    <View style={styles.infoItem}>
                      <Text style={styles.infoLabel}>Agency:</Text>
                      <Text style={styles.infoValue}>
                        {report.buying_agent.agency_name}
                      </Text>
                    </View>
                  )}
                </>
              )}
            </View>
          </View>
        </View>

        {/* Summary Row: Repair List Summary + Addendum Summary */}
        <View style={styles.summaryRow}>
          <View
            style={[
              styles.summaryBox,
              { backgroundColor: lightBg, borderColor: primaryColor },
            ]}
          >
            <Text style={styles.summaryTitle}>
              Repair List Summary - {repairItems.length} Items
            </Text>
            <Text style={styles.summaryText}>
              This document contains a list of items identified during the
              inspection. Each item includes title, description, photos, and
              your notes.
            </Text>
          </View>
          {hasAddendum && (
            <View style={styles.addendumSummaryBox}>
              <Text style={styles.addendumSummaryTitle}>Addendum Summary</Text>
              {addendumRepairCount > 0 && (
                <View style={styles.addendumSummaryRepair}>
                  <Text style={styles.addendumSummaryRepairText}>
                    {addendumRepairCount} repair item
                    {addendumRepairCount !== 1 ? 's' : ''} requested
                  </Text>
                </View>
              )}
              {addendumCreditCount > 0 && (
                <View style={styles.addendumSummaryCredit}>
                  <Text style={styles.addendumSummaryCreditText}>
                    {addendumCreditCount} credit
                    {addendumCreditCount !== 1 ? 's' : ''} requested
                  </Text>
                  <Text style={styles.addendumSummaryCreditAmount}>
                    $
                    {addendumCreditTotal.toLocaleString('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}{' '}
                    total
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Repair Items */}
        <View style={styles.itemsSection}>
          <Text
            style={[styles.sectionTitle, { borderBottomColor: primaryColor }]}
          >
            Repair Items
          </Text>

          {(() => {
            // Group items by report
            const grouped = repairItems.reduce(
              (acc, item) => {
                const key = item.reportId || 'legacy';
                if (!acc[key]) {
                  acc[key] = {
                    reportName: item.reportName || 'Legacy Items',
                    items: [],
                  };
                }
                acc[key].items.push(item);
                return acc;
              },
              {} as Record<string, { reportName: string; items: typeof repairItems }>
            );

            // Sort items by defectIdx within each group
            const sortByDefectIdx = (a: RepairItem, b: RepairItem) => {
              const aParts = (repairItemDefectIdx(a) || '').split('.').map(Number);
              const bParts = (repairItemDefectIdx(b) || '').split('.').map(Number);
              for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
                const diff = (aParts[i] || 0) - (bParts[i] || 0);
                if (diff !== 0) return diff;
              }
              return 0;
            };

            const groupEntries = Object.entries(grouped);
            const hasMultipleReports = groupEntries.length > 1;

            return groupEntries.map(([reportId, { reportName, items }]) => (
              <View key={reportId}>
                {hasMultipleReports && (
                  <View style={[styles.reportGroupHeader, { borderLeftColor: primaryColor }]}>
                    <Text style={styles.reportGroupTitle}>{reportName}</Text>
                  </View>
                )}
                {[...items].sort(sortByDefectIdx).map((item, index) => {
                  const thumbs = repairItemThumbs(item);
                  const textHtml = repairItemTextHtml(item);
                  const location = repairItemLocation(item);
                  return (
                  <View key={item.id} style={styles.repairItem} wrap={thumbs.length > 3}>
                    {/* Item Header */}
                    <View style={styles.itemHeader}>
                      <View style={styles.itemInfo}>
                        <Text style={styles.itemName}>
                          {repairItemDefectIdx(item) || index + 1}. {repairItemName(item)}
                        </Text>
                        {location && (
                          <Text style={styles.itemLocation}>
                            Location: {location}
                          </Text>
                        )}
                        <Text style={[styles.itemSection, { color: primaryColor }]}>
                          Section: {item.sectionName}
                        </Text>
                      </View>
                    </View>

                    {/* Photo Grid */}
                    {thumbs.length > 0 && (
                      <>
                        <View style={styles.photoGrid}>
                          {thumbs.map((thumb, photoIndex) => (
                            <View
                              key={thumb.id || photoIndex}
                              style={
                                photoIndex % 3 === 2
                                  ? styles.photoGridItemLast
                                  : styles.photoGridItem
                              }
                            >
                              <Image
                                style={styles.gridPhoto}
                                src={proxyIfCrossOrigin(thumb.url)}
                              />
                              {thumb.isVideo && (
                                <View style={styles.videoIndicator}>
                                  <Text style={styles.videoIndicatorText}>▶</Text>
                                </View>
                              )}
                              {thumb.caption && thumb.caption.trim() && (
                                <Text style={styles.photoCaption} wrap>
                                  {stripHtml(thumb.caption)}
                                </Text>
                              )}
                            </View>
                          ))}
                        </View>

                        {/* Video Links */}
                        {thumbs.some((t) => t.isVideo) && (
                          <View
                            style={[
                              styles.videoLinks,
                              { backgroundColor: veryLightBg },
                            ]}
                          >
                            <Text
                              style={[styles.videoLinksTitle, { color: darkColor }]}
                            >
                              Videos:
                            </Text>
                            {thumbs.filter((t) => t.isVideo).map((video, videoIndex) => (
                              <Text
                                key={video.id || videoIndex}
                                style={[styles.videoLink, { color: primaryColor }]}
                              >
                                •{' '}
                                <Link
                                  src={video.url}
                                  style={[
                                    styles.videoLinkText,
                                    { color: primaryColor },
                                  ]}
                                >
                                  View Video {videoIndex + 1}
                                </Link>
                              </Text>
                            ))}
                          </View>
                        )}
                      </>
                    )}

                    {/* Item Content */}
                    <View style={styles.itemContent}>
                      {isSpectoraRepairItem(item) &&
                        item.value &&
                        item.answer_type === 'checkbox' && (
                        <Text style={styles.itemValue}>{item.value}</Text>
                      )}

                      {textHtml && (
                        <Text style={styles.itemText}>
                          Description: {stripHtml(textHtml)}
                        </Text>
                      )}

                      {includeNotes && item.notes && (
                        <Text style={styles.itemNotes}>
                          Notes: {stripHtml(item.notes)}
                        </Text>
                      )}
                    </View>

                    {/* Addendum: request repair or credit */}
                    {(item.addendumType === 'repair' || item.addendumType === 'credit') && (
                      <View style={styles.addendumBox}>
                        <Text style={styles.addendumLabel}>Request from seller</Text>
                        {item.addendumType === 'repair' && (
                          <View style={styles.addendumRepair}>
                            <Text style={styles.addendumRepairTitle}>Request repair</Text>
                            <Text style={styles.addendumRepairSub}>
                              Asking seller to have this item repaired
                            </Text>
                          </View>
                        )}
                        {item.addendumType === 'credit' && (
                          <View style={styles.addendumCredit}>
                            <View style={styles.addendumCreditLeft}>
                              <Text style={styles.addendumCreditTitle}>Request credit</Text>
                              <Text style={styles.addendumCreditSub}>
                                Asking seller for a credit amount
                              </Text>
                            </View>
                            {item.addendumCreditAmount != null && item.addendumCreditAmount > 0 && (
                              <Text style={styles.addendumCreditAmount}>
                                ${Number(item.addendumCreditAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </Text>
                            )}
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                );
                })}
              </View>
            ));
          })()}
        </View>

        {/* Footer */}
        <Text style={styles.footer}>
          Generated on {new Date().toLocaleDateString()} • {company.name}
        </Text>
      </Page>
    </Document>
  );
};

export default RepairListPDF;
